import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Chip, TextField,
  InputAdornment, CircularProgress, ToggleButton, ToggleButtonGroup, Button,
  Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CodeIcon from '@mui/icons-material/Code';
import ShieldIcon from '@mui/icons-material/Shield';
import TuneIcon from '@mui/icons-material/Tune';
import AppCard from '../components/AppCard';
import AppViewer from '../components/AppViewer';
import { useApps } from '../hooks/useApps';
import { useProjects } from '../hooks/useProjects';
import { APPLAB_ACCENT, APPLAB_ORANGE, CATEGORIES } from '../constants';

// ── AppLAB logotyp ─────────────────────────────────────────────────────────────
export function AppLabLogo() {
  return (
    <Typography
      component="span"
      sx={{
        fontSize: { xs: '2.8rem', sm: '4rem', md: '5.5rem' },
        fontWeight: 900,
        lineHeight: 1.0,
        letterSpacing: '-2px',
        color: 'white',
        display: 'block',
        textShadow: '0 2px 32px rgba(0,0,0,0.6)',
      }}
    >
      App<span style={{ color: APPLAB_ORANGE }}>LAB</span>
    </Typography>
  );
}

// ── Pipeline modules ───────────────────────────────────────────────────────────
const PIPELINE = [
  { label: 'MappN',   sub: 'Kartlägg systemarkitektur & plattformar',           color: '#1565C0', emoji: '🗺️', route: '/applab/mappn'  },
  { label: 'MockupN', sub: 'Visualisera komponenter för kund eller team',        color: APPLAB_ACCENT, emoji: '✨', route: '/applab/mockup' },
  { label: 'Verktyg', sub: 'Bygg en körbar app som löser ett specifikt behov',   color: '#E65100', emoji: '🔧', route: '/applab/upload' },
];

// ── Capabilities ───────────────────────────────────────────────────────────────
const CAPABILITIES = [
  {
    icon: <CodeIcon sx={{ fontSize: 28, color: APPLAB_ACCENT }} />,
    title: 'Bygg med AI',
    text: 'Skapa HTML-appar med AI-stöd via AppLAB SDK. Anropa Claude direkt från din kod med ett API-anrop.',
  },
  {
    icon: <CloudUploadIcon sx={{ fontSize: 28, color: APPLAB_ACCENT }} />,
    title: 'Ladda upp & dela',
    text: 'Ladda upp en HTML-fil eller ZIP och publicera direkt – appen är tillgänglig för hela teamet på sekunder.',
  },
  {
    icon: <ShieldIcon sx={{ fontSize: 28, color: APPLAB_ACCENT }} />,
    title: 'Sandboxad miljö',
    text: 'Appar körs isolerat på applab.lagbasn.se med strikt kontrollerad access till AI-block och användardata.',
  },
  {
    icon: <TuneIcon sx={{ fontSize: 28, color: APPLAB_ACCENT }} />,
    title: 'Kvothantering',
    text: 'Adminpanelen ger full kontroll över token-förbrukning och modellval per användare.',
  },
];

// ── Main component ─────────────────────────────────────────────────────────────
export default function AppLabLanding() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { apps, loading } = useApps();
  const { projects } = useProjects();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Alla');
  const [statusFilter, setStatusFilter] = useState('alla');
  const [uppdragFilter, setUppdragFilter] = useState(null);
  const [viewerApp, setViewerApp] = useState(null);
  const videoRef = useRef(null);
  const appsRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Resolve URL param → uppdrag object once projects are loaded
  useEffect(() => {
    const paramId = searchParams.get('uppdrag');
    if (paramId && projects.length > 0 && (!uppdragFilter || uppdragFilter.id !== paramId)) {
      const found = projects.find(p => p.id === paramId);
      setUppdragFilter(found || null);
    }
    if (!paramId && uppdragFilter) setUppdragFilter(null);
  }, [projects, searchParams, uppdragFilter]);

  function handleUppdragFilter(val) {
    setUppdragFilter(val);
    setSearchParams(prev => {
      if (val) prev.set('uppdrag', val.id);
      else prev.delete('uppdrag');
      return prev;
    });
  }

  const uppdragParamId = searchParams.get('uppdrag');

  function matchesFilter(app) {
    const matchSearch = !search ||
      (app.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (app.tagline || '').toLowerCase().includes(search.toLowerCase());
    const matchCategory = category === 'Alla' || app.category === category;
    const matchStatus = statusFilter === 'alla' || app.status === statusFilter;
    const matchUppdrag = !uppdragParamId ||
      (app.uppdrag || []).some(u => u.id === uppdragParamId);
    return matchSearch && matchCategory && matchStatus && matchUppdrag;
  }

  const uploadedApps = apps.filter(a => (a.type === 'html_upload' || a.type === 'mockup') && matchesFilter(a));
  const builtinApps  = apps.filter(a => a.type !== 'html_upload' && a.type !== 'mockup' && matchesFilter(a));

  function handleAppClick(app) {
    if (app.status === 'coming_soon') return;
    if (app.type === 'mockup') { navigate(`/applab/mockup/${app.id}/edit`); return; }
    if (app.type === 'html_upload') { setViewerApp(app); return; }
    if (app.route) navigate(app.route);
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#111', color: 'white' }}>
      <AppViewer app={viewerApp} onClose={() => setViewerApp(null)} />

      {/* ── Hero ──────────────────────────────────────────────────────────────── */}
      <Box sx={{
        position: 'relative', width: '100%', height: '100vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
      }}>
        {/* Video background */}
        <Box
          component="video"
          ref={videoRef}
          autoPlay loop muted playsInline preload="auto"
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
        >
          <source src="/applab-hero.webm" type="video/webm" />
          <source src="/applab-hero.mp4" type="video/mp4" />
        </Box>

        {/* Gradient overlay */}
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 40%, rgba(17,17,17,0.92) 100%)',
        }} />

        {/* Back to Dashboard */}
        <Box
          onClick={() => navigate('/dashboard')}
          sx={{
            position: 'relative', zIndex: 2,
            display: 'flex', alignItems: 'center', gap: 1,
            cursor: 'pointer', color: 'rgba(255,255,255,0.6)',
            fontSize: '0.85rem', px: 3, pt: 3, alignSelf: 'flex-start',
            '&:hover': { color: 'white' },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
          Dashboard
        </Box>

        {/* Hero content */}
        <Box sx={{
          position: 'relative', zIndex: 2,
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          textAlign: 'center', px: 2,
        }}>
          <Chip
            icon={<RocketLaunchIcon sx={{ fontSize: '0.9rem !important', color: `${APPLAB_ACCENT} !important` }} />}
            label="Intern appkatalog"
            size="small"
            sx={{
              mb: 3,
              bgcolor: APPLAB_ACCENT + '22',
              color: APPLAB_ACCENT,
              border: `1px solid ${APPLAB_ACCENT}55`,
              fontWeight: 700,
              fontSize: '0.7rem',
              backdropFilter: 'blur(8px)',
            }}
          />
          <AppLabLogo />
          <Typography
            variant="h6"
            sx={{ mt: 2, color: 'rgba(255,255,255,0.65)', maxWidth: 540, fontWeight: 400 }}
          >
            Ditt interna labb för att bygga, testa och dela AI-drivna appar med dina kollegor.
          </Typography>
          <Button
            onClick={() => appsRef.current?.scrollIntoView({ behavior: 'smooth' })}
            variant="contained"
            sx={{
              mt: 4,
              bgcolor: APPLAB_ACCENT,
              color: 'white',
              fontWeight: 700,
              px: 4,
              py: 1.2,
              borderRadius: 2,
              fontSize: '0.9rem',
              textTransform: 'none',
              '&:hover': { bgcolor: '#00796B' },
            }}
          >
            Kom igång
          </Button>
        </Box>
      </Box>

      <Container ref={appsRef} maxWidth="lg" sx={{ py: 6 }}>

        {/* ── Pipeline ─────────────────────────────────────────────────────── */}
        <Box sx={{ mb: 7 }}>
          <Box sx={{ mb: 3 }}>
            <Typography fontWeight={800} fontSize="1rem" sx={{ color: 'white', mb: 0.25 }}>
              Pipeline
            </Typography>
            <Typography fontSize="0.72rem" sx={{ color: '#555' }}>
              Kartlägg → Visualisera → Bygg
            </Typography>
          </Box>

          {/* Step circles — desktop */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mb: 2.5, px: 4 }}>
            {PIPELINE.map((mod, i) => (
              <React.Fragment key={mod.label}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{
                    width: 46, height: 46, borderRadius: '50%',
                    background: `linear-gradient(135deg, ${mod.color}, ${mod.color}99)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontWeight: 900, fontSize: '1.15rem',
                    boxShadow: `0 4px 18px ${mod.color}44`,
                    border: `2px solid ${mod.color}cc`,
                  }}>
                    {i + 1}
                  </Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: mod.color, whiteSpace: 'nowrap' }}>
                    {mod.label}
                  </Typography>
                </Box>
                {i < PIPELINE.length - 1 && (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', pb: '1.75rem', mx: 1 }}>
                    <Box sx={{
                      flex: 1, height: 2,
                      background: `linear-gradient(90deg, ${PIPELINE[i].color}44, ${PIPELINE[i + 1].color}44)`,
                    }} />
                    <Typography sx={{ color: '#555', fontSize: '1.2rem', lineHeight: 1, ml: 0.5 }}>›</Typography>
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>

          {/* Cards */}
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            {PIPELINE.map((mod, i) => (
              <React.Fragment key={mod.label}>
                <Box
                  onClick={() => navigate(mod.route)}
                  sx={{
                    flex: 1, p: 2.5, borderRadius: 2, cursor: 'pointer',
                    border: `1px solid ${mod.color}33`,
                    bgcolor: mod.color + '0d',
                    transition: 'all 0.18s',
                    '&:hover': {
                      bgcolor: mod.color + '1a', borderColor: mod.color + '66',
                      transform: 'translateY(-2px)', boxShadow: `0 8px 28px ${mod.color}22`,
                    },
                    display: 'flex', flexDirection: 'column', gap: 0.75,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.25 }}>
                    <Typography sx={{ fontSize: '1.8rem', lineHeight: 1 }}>{mod.emoji}</Typography>
                    <Box sx={{
                      display: { xs: 'flex', md: 'none' },
                      width: 22, height: 22, borderRadius: '50%', bgcolor: mod.color,
                      alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: 900, fontSize: '0.7rem',
                    }}>
                      {i + 1}
                    </Box>
                  </Box>
                  <Typography fontWeight={800} sx={{ color: 'white' }}>{mod.label}</Typography>
                  <Typography fontSize="0.75rem" sx={{ color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>
                    {mod.sub}
                  </Typography>
                </Box>
                {i < PIPELINE.length - 1 && (
                  <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', color: '#444', fontSize: '1.3rem' }}>
                    ↓
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        {/* ── Capabilities ──────────────────────────────────────────────────── */}
        <Grid container spacing={3} sx={{ mb: 7 }}>
          {CAPABILITIES.map((cap) => (
            <Grid item xs={12} sm={6} key={cap.title}>
              <Box sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.07)',
                bgcolor: 'rgba(255,255,255,0.03)',
                height: '100%',
              }}>
                <Box sx={{ mb: 1.5 }}>{cap.icon}</Box>
                <Typography fontWeight={700} fontSize="0.9rem" sx={{ mb: 0.5 }}>
                  {cap.title}
                </Typography>
                <Typography fontSize="0.8rem" sx={{ color: '#888', lineHeight: 1.6 }}>
                  {cap.text}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* ── Filter bar ────────────────────────────────────────────────────── */}
        <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { md: 'center' } }}>
          <TextField
            placeholder="Sök app..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            sx={{
              minWidth: 220,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255,255,255,0.05)',
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                '&:hover fieldset': { borderColor: APPLAB_ACCENT },
                '&.Mui-focused fieldset': { borderColor: APPLAB_ACCENT },
              },
              '& input::placeholder': { color: '#666' },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#666', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Uppdrag filter */}
          <Autocomplete
            options={projects}
            value={uppdragFilter}
            onChange={(_, val) => handleUppdragFilter(val)}
            getOptionLabel={o => o ? `${o.projectNumber} · ${o.name}` : ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            filterOptions={(opts, { inputValue }) => {
              const q = inputValue.toLowerCase();
              if (!q) return opts.slice(0, 10);
              return opts.filter(o =>
                (o.projectNumber || '').toLowerCase().includes(q) ||
                (o.name || '').toLowerCase().includes(q)
              ).slice(0, 10);
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Typography fontSize="0.82rem" sx={{ color: 'white' }}>
                  <span style={{ color: APPLAB_ACCENT, fontWeight: 700 }}>{option.projectNumber}</span>
                  {' · '}{option.name}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Filtrera på uppdrag…"
                size="small"
                sx={{
                  minWidth: 240,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.05)', color: 'white',
                    '& fieldset': { borderColor: uppdragFilter ? APPLAB_ACCENT + '88' : 'rgba(255,255,255,0.15)' },
                    '&:hover fieldset': { borderColor: APPLAB_ACCENT },
                    '&.Mui-focused fieldset': { borderColor: APPLAB_ACCENT },
                  },
                  '& input::placeholder': { color: '#666' },
                }}
              />
            )}
            componentsProps={{ paper: { sx: { bgcolor: '#1e1e1e', color: 'white', border: '1px solid rgba(255,255,255,0.08)' } } }}
            noOptionsText={<span style={{ color: '#888', fontSize: '0.82rem' }}>Inga matchande uppdrag</span>}
          />

          {/* Category chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {CATEGORIES.map(cat => (
              <Chip
                key={cat}
                label={cat}
                size="small"
                onClick={() => setCategory(cat)}
                sx={{
                  bgcolor: category === cat ? APPLAB_ACCENT + '33' : 'rgba(255,255,255,0.06)',
                  color: category === cat ? APPLAB_ACCENT : '#888',
                  border: `1px solid ${category === cat ? APPLAB_ACCENT + '55' : 'transparent'}`,
                  fontWeight: category === cat ? 700 : 400,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: APPLAB_ACCENT + '22', color: APPLAB_ACCENT },
                }}
              />
            ))}
          </Box>

          {/* Status toggle */}
          <Box sx={{ ml: { md: 'auto' } }}>
            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              onChange={(_, v) => v && setStatusFilter(v)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  color: '#888',
                  borderColor: 'rgba(255,255,255,0.12)',
                  fontSize: '0.7rem',
                  py: 0.5, px: 1.5,
                  '&.Mui-selected': { color: APPLAB_ACCENT, bgcolor: APPLAB_ACCENT + '22', borderColor: APPLAB_ACCENT + '44' },
                },
              }}
            >
              <ToggleButton value="alla">Alla</ToggleButton>
              <ToggleButton value="active">Aktiva</ToggleButton>
              <ToggleButton value="beta">Beta</ToggleButton>
              <ToggleButton value="coming_soon">Snart</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* ── Skapade i AppLAB ──────────────────────────────────────────────── */}
        {!loading && (uploadedApps.length > 0 || !search) && (
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography fontWeight={800} fontSize="1rem" sx={{ color: 'white' }}>
                  Skapade i App<span style={{ color: APPLAB_ORANGE }}>LAB</span>
                </Typography>
                {uploadedApps.length > 0 && (
                  <Chip
                    label={uploadedApps.length}
                    size="small"
                    sx={{ bgcolor: APPLAB_ACCENT + '22', color: APPLAB_ACCENT, fontWeight: 700, fontSize: '0.7rem', height: 20 }}
                  />
                )}
              </Box>
              <Typography
                fontSize="0.78rem"
                onClick={() => navigate('/applab/new')}
                sx={{ color: APPLAB_ACCENT, cursor: 'pointer', fontWeight: 600, '&:hover': { opacity: 0.8 } }}
              >
                + Ny app
              </Typography>
            </Box>

            {uploadedApps.length === 0 ? (
              <Box
                onClick={() => navigate('/applab/new')}
                sx={{
                  p: 3, borderRadius: 2, cursor: 'pointer',
                  border: `1px dashed rgba(255,255,255,0.1)`,
                  bgcolor: 'rgba(255,255,255,0.02)',
                  textAlign: 'center',
                  '&:hover': { borderColor: APPLAB_ACCENT + '66', bgcolor: APPLAB_ACCENT + '08' },
                  transition: 'all 0.15s',
                }}
              >
                <Typography fontSize="0.85rem" sx={{ color: '#555' }}>
                  Inga egna appar ännu — skapa en MockupN eller ladda upp en HTML-fil.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2.5}>
                {uploadedApps.map(app => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={app.id}>
                    <AppCard app={app} onClick={() => handleAppClick(app)} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* ── Övriga appar ──────────────────────────────────────────────────── */}
        {!loading && builtinApps.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {uploadedApps.length > 0 && (
              <Typography fontWeight={800} fontSize="1rem" sx={{ color: 'white', mb: 2 }}>
                LagbasN-moduler
              </Typography>
            )}
            <Grid container spacing={2.5}>
              {builtinApps.map(app => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={app.id}>
                  <AppCard app={app} onClick={() => handleAppClick(app)} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: APPLAB_ACCENT }} />
          </Box>
        )}

        {!loading && uploadedApps.length === 0 && builtinApps.length === 0 && search && (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography sx={{ color: '#555' }}>Inga appar matchar din sökning.</Typography>
          </Box>
        )}

        {/* ── Footer ────────────────────────────────────────────────────────── */}
        <Box sx={{ mt: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
          <Typography fontSize="0.75rem" sx={{ color: '#444' }}>
            App<span style={{ color: APPLAB_ORANGE }}>LAB</span> · LagbasN intern verktygsplattform
          </Typography>
          <Typography
            fontSize="0.75rem"
            onClick={() => navigate('/applab/docs')}
            sx={{ color: '#555', cursor: 'pointer', '&:hover': { color: APPLAB_ACCENT } }}
          >
            Dokumentation →
          </Typography>
          <Typography
            fontSize="0.75rem"
            onClick={() => navigate('/applab/new')}
            sx={{ color: '#555', cursor: 'pointer', '&:hover': { color: APPLAB_ACCENT } }}
          >
            Ny app →
          </Typography>
        </Box>

      </Container>
    </Box>
  );
}
